/**
 * RosterPage.js
 * 
 * Displays the top 25 most recently active pilots in a modern, aviation-themed UI.
 * 
 * Features:
 * - Responsive table layout for desktop
 * - Card-based layout for mobile devices
 * - Dark/light mode support via props.darkmode
 * - Accessible with ARIA labels and keyboard navigation
 * - Animated loading states and smooth transitions
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.page - Current page identifier
 * @param {boolean} props.darkmode - Whether dark mode is enabled
 */

import React, { useState, useEffect } from "react";
import axios from "axios";

import TopNav from "../components/TopNav";
import BackButton from "../components/BackButton";
import InsigniaRank from "../components/InsigniaRank";
import "./RosterPage.css";

/**
 * Maps insignia codes to human-readable rank names
 * @param {string} insigniaCode - The insignia code (e.g., "C5", "FO1")
 * @returns {string} Human-readable rank name
 */
const getInsigniaRankName = (insigniaCode) => {
    const rankMap = {
        'NH': 'New Hire',
        'FOA': 'Second Officer',
        'FO1': 'Junior First Officer',
        'FO2': 'First Officer',
        'FO3': 'Senior First Officer',
        'C1': 'Captain',
        'C2': 'Ferry Captain',
        'C3': 'Charter Captain',
        'C4': 'Merit Captain',
        'C5': 'Senior Captain',
        'C6': 'Senior Captain II',
        'C7': 'Senior Captain III',
        'C8': 'Elite Senior Captain',
        'C9': 'Grand Senior Captain',
    };
    return rankMap[insigniaCode] || 'Pilot';
};

/**
 * Formats a Unix timestamp to a readable date string
 * @param {string|number} timestamp - Unix timestamp
 * @returns {string} Formatted date string
 */
const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

/**
 * Formats a number with thousand separators
 * @param {string|number} num - Number to format
 * @returns {string} Formatted number string
 */
const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return parseInt(num).toLocaleString('en-US');
};

/**
 * Extracts pilot name and callsign from full_name field
 * @param {string} fullName - Full name string (e.g., "John Doe, FDX123")
 * @returns {Object} Object with name and callsign properties
 */
const parsePilotName = (fullName) => {
    if (!fullName) return { name: 'Unknown', callsign: '' };
    const parts = fullName.split(', ');
    return {
        name: parts[0] || 'Unknown',
        callsign: parts[1] || ''
    };
};

const RosterPage = (props) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [data, setData] = useState([]);

    // Determine theme class based on darkmode prop
    const themeClass = props.darkmode ? 'dark-theme' : 'light-theme';

    // Get the data. First check local storage. If not there, get it from the server.
    useEffect(() => {
        const localData = localStorage.getItem("roster");

        if (localData) {
            setData(JSON.parse(localData));
            console.log("Data from local storage");
            console.log(JSON.parse(localData));
        } else {
            getData();
            console.log("Data from server");
            console.log(data);
        }
         return () => {}
    }, []);
    
    const getData = async (obj) => {
      try {
        setLoading(true);
        setError(false);

        const response = await axios.get("/api/get_roster", {
        params: { _t: Date.now() }
        });                                
        console.log(response);
        const resObj = response.data;
        if (resObj) {
            setData(resObj);
            console.log(resObj);
            setLoading(false);
        } else {
          console.log("No data");
        }
      } catch (e) {
        console.log(e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    /**
     * Renders the statistics summary bar
     */
    const renderStatsBar = () => {
        if (!data || !data.meta) return null;

        // Aggregate totals across the currently displayed roster list
        const totals = (Array.isArray(data.roster) ? data.roster : []).reduce(
            (acc, p) => {
                acc.flights += Number(p?.flights || 0);
                acc.hours += Number(p?.hours || 0);
                return acc;
            },
            { flights: 0, hours: 0 }
        );
        
        return (
            <div className="roster-stats-bar" role="region" aria-label="Roster Statistics">
                <div className="roster-stat-item">
                    <span className="roster-stat-value">{formatNumber(data.meta.count)}</span>
                    <span className="roster-stat-label">Total Pilots</span>
                </div>
                <div className="roster-stat-item">
                    <span className="roster-stat-value">{formatNumber(data.meta.active)}</span>
                    <span className="roster-stat-label">Active Pilots</span>
                </div>
                <div className="roster-stat-item">
                    <span className="roster-stat-value">{formatNumber(totals.flights)}</span>
                    <span className="roster-stat-label">Total Flights</span>
                </div>
                <div className="roster-stat-item">
                    <span className="roster-stat-value">{formatNumber(totals.hours)}</span>
                    <span className="roster-stat-label">Total Hours</span>
                </div>
                <div className="roster-stat-item">
                    <span className="roster-stat-value">{data.roster ? data.roster.length : 0}</span>
                    <span className="roster-stat-label">Shown</span>
                </div>
            </div>
        );
    };

    /**
     * Renders the desktop table view
     */
    const renderTable = () => {
        if (!data || !data.roster || !Array.isArray(data.roster)) return null;

        return (
            <div className="roster-table-container">
                <table 
                    className="roster-table" 
                    role="table" 
                    aria-label="Pilot Roster - Top 25 Most Recently Active"
                >
                    <thead>
                        <tr role="row">
                            <th scope="col" role="columnheader">#</th>
                            <th scope="col" role="columnheader">Rank</th>
                            <th scope="col" role="columnheader">Pilot</th>
                            <th scope="col" role="columnheader">AWO</th>
                            <th scope="col" role="columnheader" className="roster-numeric">Hours</th>
                            <th scope="col" role="columnheader" className="roster-numeric">Flights</th>
                            <th scope="col" role="columnheader">Active</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.roster.map((pilot, index) => {
                            const { name, callsign } = parsePilotName(pilot.full_name);
                            const rankName = getInsigniaRankName(pilot.insignia);
                            //const rankClass = index < 3 ? `rank-${index + 1}` : '';
                            const isCaller = String(pilot.iid || '') === String(data?.meta?.caller || '');
                            
                            return (
                                <tr 
                                    key={pilot.iid || `pilot-${index}`} 
                                    role="row"
                                    tabIndex="0"
                                    aria-label={`Rank ${index + 1}: ${name}, ${rankName}`}
                                    className={isCaller ? 'roster-row-caller' : undefined}
                                >
                                     <td className="isrank-cell">
                                        <InsigniaRank rank={rankName} />
                                     </td>

                                     <td role="cell" className="roster-insignia-cell">
                                        <div className="roster-insignia-content">
                                            {/* <InsigniaRank rank={rankName} /> */}
                                            <span className="roster-insignia-label">{rankName}</span>
                                        </div>
                                     </td>
                                    <td role="cell">
                                        <div className="roster-pilot-name">
                                            <span>{name}</span>
                                            {callsign && (
                                                <span className="roster-pilot-callsign">{callsign}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td role="cell">
                                        <span 
                                            className={`roster-awo-badge ${!pilot.cat23 ? 'no-awo' : ''}`}
                                            aria-label={pilot.cat23_descrip || 'No AWO certification'}
                                        >
                                            {pilot.cat23_descrip || 'None'}
                                        </span>
                                    </td>
                                    <td role="cell" className="roster-numeric">
                                        <span className="roster-hours">{formatNumber(pilot.hours)}</span>
                                    </td>
                                    <td role="cell" className="roster-numeric">
                                        <span className="roster-flights">{formatNumber(pilot.flights)}</span>
                                    </td>
                                    <td role="cell">
                                        <span className="roster-date">{formatDate(pilot.fdate)}</span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    /**
     * Renders the mobile card view
     */
    const renderCards = () => {
        if (!data || !data.roster || !Array.isArray(data.roster)) return null;

        return (
            <div 
                className="roster-cards-container" 
                role="list" 
                aria-label="Pilot Roster Cards"
            >
                {data.roster.map((pilot, index) => {
                    const { name, callsign } = parsePilotName(pilot.full_name);
                    const rankName = getInsigniaRankName(pilot.insignia);
                    const rankClass = index < 3 ? `rank-${index + 1}` : '';
                    const isCaller = String(pilot.iid || '') === String(data?.meta?.caller || '');

                    return (
                        <article 
                            key={pilot.iid || `pilot-card-${index}`}
                            className={`roster-pilot-card ${isCaller ? 'roster-card-caller' : ''}`}
                            role="listitem"
                            tabIndex="0"
                            aria-label={`Rank ${index + 1}: ${name}, ${rankName}`}
                        >
                            <span className={`roster-card-rank-badge ${rankClass}`}>
                                #{index + 1}
                            </span>
                            
                            <header className="roster-card-header">
                                <InsigniaRank rank={rankName} />
                                <div className="roster-card-pilot-info">
                                    <h3 className="roster-card-pilot-name">{name}</h3>
                                    {callsign && (
                                        <p className="roster-card-pilot-callsign">{callsign}</p>
                                    )}
                                </div>
                            </header>

                            <div className="roster-card-stats">
                                <div className="roster-card-stat">
                                    <span className="roster-card-stat-label">Rank</span>
                                    <span className="roster-card-stat-value">{rankName}</span>
                                </div>
                                <div className="roster-card-stat">
                                    <span className="roster-card-stat-label">AWO</span>
                                    <span className="roster-card-stat-value">
                                        {pilot.cat23_descrip || 'None'}
                                    </span>
                                </div>
                                <div className="roster-card-stat">
                                    <span className="roster-card-stat-label">Flight Hours</span>
                                    <span className="roster-card-stat-value hours">
                                        {formatNumber(pilot.hours)}
                                    </span>
                                </div>
                                <div className="roster-card-stat">
                                    <span className="roster-card-stat-label">Total Flights</span>
                                    <span className="roster-card-stat-value flights">
                                        {formatNumber(pilot.flights)}
                                    </span>
                                </div>
                                <div className="roster-card-stat">
                                    <span className="roster-card-stat-label">Active</span>
                                    <span className="roster-card-stat-value">
                                        {formatDate(pilot.fdate)}
                                    </span>
                                </div>
                            </div>

                            <footer className="roster-card-footer">
                                <span 
                                    className={`roster-awo-badge ${!pilot.cat23 ? 'no-awo' : ''}`}
                                >
                                    {pilot.cat23_descrip || 'No AWO'}
                                </span>
                            </footer>
                        </article>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={`roster-page-container ${themeClass}`}>
            <TopNav page={props.page} darkMode={props.darkmode} />
            <BackButton />

            {/* Loading State */}
            {loading && (
                <div 
                    className="roster-loading" 
                    role="status" 
                    aria-live="polite"
                    aria-busy="true"
                >
                    <div className="roster-loading-spinner" aria-hidden="true"></div>
                    <span className="roster-loading-text">Loading Roster Data - Please wait</span>
                    <span className="sr-only">Loading pilot roster data</span>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div
                    className="roster-error"
                    role="alert"
                    aria-live="assertive"
                >
                    <p className="roster-error-text">
                        Failed to load roster data. Please try again later.
                    </p>
                    <button
                        onClick={() => getData()}
                        className="roster-refresh-button"
                        disabled={loading}
                        aria-label="Retry loading roster data"
                    >
                        {loading ? 'Retrying...' : 'Try Again'}
                    </button>
                </div>
            )}

            {/* Main Content */}
            {!loading && !error && data && Array.isArray(data.roster) && data.roster.length > 0 && (
                <main className="roster-main" role="main">
                    {/* Header Section */}
                    <section className="roster-header-section">
                        <h1 className="roster-page-title">Pilot Roster</h1>
                        <p className="roster-page-subtitle">
                            Fly to the top! Here are the top 25 most recently active pilots, ranked by date of flight.
                        </p>
                        <div className="roster-refresh-container">
                            <button
                                onClick={() => getData()}
                                className="roster-refresh-button"
                                disabled={loading}
                                aria-label="Refresh roster data"
                            >
                                {loading ? 'Refreshing...' : 'Refresh Data'}
                            </button>
                        </div>
                        {renderStatsBar()}
                    </section>

                    {/* Desktop Table View */}
                    {renderTable()}

                    {/* Mobile Card View */}
                    {renderCards()}
                </main>
            )}

            {/* Empty State */}
            {!loading && !error && (!data || !Array.isArray(data.roster) || data.roster.length === 0) && (
                <div
                    className="roster-empty"
                    role="status"
                    aria-live="polite"
                >
                    <p className="roster-empty-text">
                        No pilot data available at this time.
                    </p>
                    <button
                        onClick={() => getData()}
                        className="roster-refresh-button"
                        disabled={loading}
                        aria-label="Refresh roster data"
                    >
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default RosterPage;
